import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@cad-arc';
import {
    ESRI_MAP_DEFAULT_ZOOM_LEVEL,
    MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY,
} from '../configurations';
import { MapView, Point, WebMap, Color } from '../constructors';
import { storageHelpers } from '../../common/helpers/browser-storage';
import { isValidLatLng } from '../../location/helpers';
import type { GeoPoint } from '../../location/types';
import type { EsriMapInterface } from '../types';
import { logEsriError } from '../helpers/utils';
import { createInitialWebMap, shouldLoadAlternativeBasemap } from './helpers';
import { useEsriMapStore } from '~/map-window/hooks/use-esri-map-store';
import { colors } from '~/colors/colors';

type UseEsriEmbeddedESModuleMap = {
    center: GeoPoint;
    mapRef: React.MutableRefObject<HTMLDivElement | null>;
};
const useEsriEmbeddedESModuleMap = ({
    center,
    mapRef,
}: UseEsriEmbeddedESModuleMap): EsriMapInterface => {
    const { mapMode: storeMapMode } = useEsriMapStore();

    const [webMap, setWebMap] = useState<WebMap>();
    const [mapView, setMapView] = useState<MapView>();
    const [mapMode, setMapMode] = useState<string>('');
    const [isWebGL2Supported, setIsWebGL2Supported] = useState<boolean>(true);
    const [isMapLoading, setIsMapLoading] = useState<boolean>(true);
    const [isMapLoadingFailed, setIsMapLoadingFailed] = useState<boolean>(false);

    const setMapLoadFailed = useCallback((failed: boolean) => {
        if (failed) {
            storageHelpers.setItem(
                MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY,
                new Date().toISOString()
            );
        }
        setIsMapLoadingFailed(failed);
    }, []);

    useEffect(
        function initializeMap() {
            if (
                mapMode === storeMapMode &&
                (!!mapView || !isValidLatLng(center.latitude, center.longitude))
            ) {
                return;
            }

            const isAlternativeBasemap = shouldLoadAlternativeBasemap();

            const webmap = createInitialWebMap({
                isAlternativeBasemap,
                isEmbeddedBasemap: true,
                mapMode: storeMapMode,
            });

            const view = new MapView({
                container: mapRef.current ?? undefined,
                map: webmap,
                center: center as Point,
                zoom: ESRI_MAP_DEFAULT_ZOOM_LEVEL,
                constraints: {
                    rotationEnabled: false,
                },
                ui: undefined,
                highlightOptions: {
                    color: new Color(colors.esriColors.highlightColor),
                    fillOpacity: 0.16,
                    haloOpacity: 1,
                },
            });

            view.when(() => {}).catch((error) => {
                setMapLoadFailed(true);
                if (error.name?.includes('webgl')) {
                    setIsWebGL2Supported(false);
                    logEsriError({
                        category: 'webgl_not_supported',
                        isEmbeddedMap: true,
                        error,
                    });
                } else {
                    logEsriError({
                        category: 'map_view_error',
                        isEmbeddedMap: true,
                        error,
                    });
                }
            });

            view.watch('ready', () => {
                setIsMapLoading(false);
            });

            setWebMap(webmap);
            setMapView(view);
            setMapMode(storeMapMode);
        },
        [mapView, setMapLoadFailed, center, mapRef, storeMapMode, mapMode]
    );

    // separate effect to destroy the esri map instance
    // because the effect above will re-trigger due to a change in
    // `mapView` and instantly destroy the map again
    useEffect(() => {
        if (!mapView) {
            return;
        }

        return () => {
            if (!mapView.destroyed) {
                mapView.destroy();
            }
        };
    }, [mapView]);

    return { mapView, webMap, isWebGL2Supported, isMapLoading, isMapLoadingFailed };
};

type UseEsriEmbeddedBaseMapProps = { center: GeoPoint };

export const useEsriEmbeddedBaseMap = ({
    center,
}: UseEsriEmbeddedBaseMapProps): EsriMapInterface & {
    mapComponent: React.ReactElement;
    mapRef: React.MutableRefObject<HTMLDivElement | null>;
} => {
    const mapRef = useRef<HTMLDivElement | null>(null);
    const mapComponent = <Box w="100%" h="100%" ref={mapRef} />;
    const { mapView, webMap, isWebGL2Supported, isMapLoading, isMapLoadingFailed } =
        useEsriEmbeddedESModuleMap({ center, mapRef });

    return {
        mapComponent,
        mapView,
        webMap,
        mapRef,
        isWebGL2Supported,
        isMapLoading,
        isMapLoadingFailed,
    };
};
